import { useEffect, useState } from 'react';

/**
 * Returns `true` only after `active` has stayed `true` continuously for
 * `delayMs`, and flips back to `false` immediately when `active` goes false.
 *
 * Use it to gate transient loading indicators so fast operations never flash
 * them — e.g. the synchronous keyword tier of category prediction resolves in
 * a few ms, so the Save-button spinner should only appear when the slower
 * cloud/LLM tail is actually running.
 */
export function useDelayedFlag(active: boolean, delayMs = 150): boolean {
  const [shown, setShown] = useState(false);

  useEffect(() => {
    // Both transitions go through a timer so we never call setState
    // synchronously in the effect body (react-hooks/set-state-in-effect):
    // showing waits the full delay, hiding fires on the next tick. `active` is
    // a dep, so the effect re-runs with the current value before each timer.
    const timer = setTimeout(() => setShown(active), active ? delayMs : 0);
    return () => clearTimeout(timer);
  }, [active, delayMs]);

  return shown;
}
